import { useState } from 'react';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useAddCreatorsToPoolMutation } from '@round/creatorbase-helpers/api-hooks';
import { Button, lucideIcons, toast } from '@round/creatorbase-ui';
import AddToPoolButton from 'Modules/Plans/CreatorPools/components/AddToPoolButton/AddToPoolButton';
import { CreatePoolModal } from 'Modules/Plans/CreatorPools/components/CreatePoolModal/CreatePoolModal';
import { BulkUploadItemRow } from '../../../../helpers';
import { formatCreatorCount, getCreatedAccountIds } from './helpers';
import styles from './AddUploadToPool.module.css';

const { ChevronDownIcon } = lucideIcons;

type Props = {
    /** The upload's created items, which the creators to add are read from. */
    rows: BulkUploadItemRow[];
    /** An upload still processing has not created all of its creators yet, so it cannot be added whole. */
    isProcessing: boolean;
};

/** Adds every creator the upload created to a pool the user picks, in one go. */
const AddUploadToPool = ({ rows, isProcessing }: Props) => {
    const [pool, setPool] = useState<creatorbase.Pool | null>(null);
    const [isCreatePoolOpen, setIsCreatePoolOpen] = useState(false);

    const accountIds = getCreatedAccountIds(rows);

    const { add, isLoading: isAdding } = useAddCreatorsToPoolMutation();

    const isDisabled = isProcessing || accountIds.length === 0;

    const handleAdd = () => {
        if (!pool) {
            return;
        }

        add(
            { poolId: pool.id, client_influencer_account_ids: accountIds },
            {
                onSuccess: () => {
                    toast.info(`${formatCreatorCount(accountIds.length)} added to ${pool.name}`);
                    setPool(null);
                },
                // The pick stays, so trying again is one click. Adding is idempotent, so a retry after a
                // request that got partway does not add anyone twice.
                onError: (error) => toast.error(creatorbaseQueries.pools.utils.addCreators.getErrorMessage(error)),
            }
        );
    };

    return (
        <div className={styles.addToPool}>
            <AddToPoolButton
                value={pool}
                onAdd={setPool}
                // The pool the user is after may not exist yet. The new pool comes back picked, so adding
                // to it is the same click as adding to any other.
                onCreateNew={() => setIsCreatePoolOpen(true)}
                disabled={isDisabled || isAdding}
                align="start"
                trigger={
                    <Button variant="outline" size="xs" aria-label="Pool" className={styles.trigger}>
                        <span className={styles.triggerLabel}>{pool?.name ?? 'Select pool'}</span>
                        <ChevronDownIcon className={styles.triggerChevron} />
                    </Button>
                }
            />

            <Button size="xs" disabled={isDisabled || !pool} isLoading={isAdding} onClick={handleAdd}>
                {`Add ${formatCreatorCount(accountIds.length)} to pool`}
            </Button>

            <CreatePoolModal isOpen={isCreatePoolOpen} onClose={() => setIsCreatePoolOpen(false)} onCreated={setPool} />
        </div>
    );
};

export default AddUploadToPool;
